import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AccountSettingsService } from '../account-setting.service';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { PhoneNumberFormatPipe } from 'src/app/libs/ui/pipes/phone-number-format/phone-number-format.pipe';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss'],
})
export class AccountDetailsComponent implements OnInit {
  /**
   * all necessary input get values from parent
   */
  @Input() formGroupName!: string;
  @Input() profileImageSrc!: string;
  @Input() dialogRef?: any; //close event of parent component
  @Input() stepper!: number;
  @Output() stepperChange = new EventEmitter<number>();
  formGroup!: FormGroup;
  private twoStepVerifyClicked = new Subject<boolean | undefined>();
  private pushNotificationClicked = new Subject<boolean | undefined>();
  private faceIdClicked = new Subject<boolean | undefined>();
  submited = false;
  name: any;
  userImgUrl: any;
  isUser: Boolean = false;
  user!: IUser;
  readonly LANG_CONST = LANG_CONST;
  readonly CONSTANTS = CONSTANTS;
  get formInstance() {
    return this.formGroup.controls;
  }
  constructor(
    private rootFormGroup: FormGroupDirective,
    private helperService: HelperService,
    private notificationService: NotificationService,
    private accountSettingService: AccountSettingsService,
    private phoneNumberFormat: PhoneNumberFormatPipe,
    private localizationService: LocalizationService,
    private translate: TranslateService
  ) {}

  /**
   * @description this is onInit life cycle method
   */
  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.formGroup = this.rootFormGroup.control.get(
      this.formGroupName
    ) as FormGroup;

    this.user = {
      userId: '',
      name: this.formGroup?.value?.name,
      userImgUrl: this.profileImageSrc,
    };

    const twoStepClickedDebounced = this.twoStepVerifyClicked.pipe(
      debounceTime(500)
    );
    twoStepClickedDebounced.subscribe((status: boolean | undefined) => {
      // The actual action that should be performed on click
      this.updateUserToggles(
        this.formInstance?.faceId?.value,
        status!,
        this.formInstance?.pushNotificationEnabled?.value,
        CONSTANTS.TWO_STEP_VERIFICATION
      );
    });

    const faceIdClickedDebounced = this.faceIdClicked.pipe(debounceTime(500));
    faceIdClickedDebounced.subscribe((status: boolean | undefined) => {
      // The actual action that should be performed on click
      this.updateUserToggles(
        status!,
        this.formInstance?.twoStepVerification?.value,
        this.formInstance?.pushNotificationEnabled?.value,
        CONSTANTS.FACE_ID
      );
    });

    const pushNotificationClickedDebounced = this.pushNotificationClicked.pipe(
      debounceTime(500)
    );
    pushNotificationClickedDebounced.subscribe(
      (status: boolean | undefined) => {
        // The actual action that should be performed on click
        this.updateUserToggles(
          this.formInstance?.faceId?.value,
          this.formInstance?.twoStepVerification?.value,
          status!,
          CONSTANTS.PUSH_NOTIFICATION
        );
      }
    );

    this.setPhoneNumber();
  }

  /**
   * @description setPhoneNumber function validates phone number and apply masking on the number.
   */

  setPhoneNumber(): void {
    this.helperService
      .validatePhoneFormat(this.formInstance?.phone?.value)
      .subscribe(
        ({
          phoneNumber,
          region,
          code,
        }: {
          phoneNumber: string;
          region: string;
          code: string;
        }) => {
          this.formInstance.phone.setValue(
            code + this.phoneNumberFormat.transform(phoneNumber, code)
          );
        },
        (err: string) => {
          if (window.__dev__) console.log('validatePhoneFormat', err);
          this.notificationService?.showError(err);
          this.formInstance?.phone?.setValue('');
        }
      );
  }

  /**
   * @description close modal dialogue with help of material service
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef?.close();
  }

  /**
   * @description navigate to back screen
   * @returns void
   */
  navigateBack(): void {
    this.stepperChange.emit((this.stepper += -1));
  }

  /**
   * @description validate form if form hold valid value into main form and redirect to next step
   * @returns void
   */
  submit(): void {
    this.submited = true;
    if (this.formGroup?.invalid) return;
  }

  /**
   * @description validate form if form valid hold value into main form and redirect to next step
   * @returns void
   */
  navigateToChangePassword(): void {
    this.stepperChange.emit((this.stepper = 2));
  }

  /**
   * @description Active Inactive two verification
   * @param status MatSlideToggleChange object  {source: MatSlideToggle, checked: true}
   * @returns void
   */
  changeTwoStepVeification(matSlide: {
    source: MatSlideToggle | undefined;
    checked: boolean | undefined;
  }): void {
    this.twoStepVerifyClicked.next(matSlide?.checked); // trigger button click after debounce
  }

  /**
   * @description Active Inactive push notification
   * @param status MatSlideToggleChange object  {source: MatSlideToggle, checked: true}
   * @returns void
   */
  changePushNotificationEnabled(matSlide: {
    source: MatSlideToggle | undefined;
    checked: boolean | undefined;
  }) {
    this.pushNotificationClicked.next(matSlide?.checked); // trigger button click after debounce
  }

  /**
   * @description validate form if form hold value into main form and redirect to navigateToChangeName step
   * @returns void
   */
  navigateToChangeName(): void {
    this.stepperChange.emit((this.stepper = 3));
  }

  /**
   * @description validate form if form hold value into main form and redirect to navigateToChangePhoneNumber step
   * @returns void
   */
  navigateToChangePhoneNumber(): void {
    this.stepperChange.emit((this.stepper = 4));
  }

  /**
   * @description navigate to delete account confirmation step
   * @returns void
   */
  navigateToDeleteAccount(): void {
    this.stepperChange.emit((this.stepper = 7));
  }

  /**
   * @description Active Inactive two verification
   * @param status MatSlideToggleChange object  {source: MatSlideToggle, checked: true}
   * @returns void
   */
  changeFaceId(matSlide: {
    source: MatSlideToggle | undefined;
    checked: boolean | undefined;
  }): void {
    this.faceIdClicked.next(matSlide?.checked); // trigger button click after debounce
  }

  updateUserToggles(
    faceId: boolean,
    twoStepVerification: boolean,
    pushNotification: boolean,
    currentState: string
  ) {
    // The actual action that should be performed on click
    this.accountSettingService
      .updateUser(faceId, twoStepVerification, pushNotification)
      .subscribe({
        next: () => {
          if (currentState === CONSTANTS.FACE_ID) {
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED_FACE_ID')
            );
          } else if (currentState === CONSTANTS.TWO_STEP_VERIFICATION) {
            this.notificationService.showSuccess(
              this.translate.instant(
                'SUCCESSFULLY_UPDATED_TWO_STEP_VERIFICATION'
              )
            );
          } else if (currentState === CONSTANTS.PUSH_NOTIFICATION) {
            this.notificationService.showSuccess(
              this.translate.instant('SUCCESSFULLY_UPDATED_PUSH_NOTIFICATION')
            );
          }
        },
        error: (err: Error) => {
          if (window.__dev__) console.log('updateUser', err);
          this.notificationService.showError(err?.message);
        },
      });
  }
  // this function is not being called from Html as we have removed the profile picture upload logic - MPC-9535
  imageClickHandler(): void {
    this.stepperChange.emit((this.stepper = 6));
  }
}
